import React, { useContext, useEffect } from 'react';
import {
  Button,
  InputField,
  ErrorMessage,
  Checkbox,
  Paragraph,
  Loader,
  Link,
} from '@sentara-health/optimahealth_web';
import { ProfileHeaderContent } from 'templates';
import { t } from 'i18next';
import PrimaryPhoneNumberComponent from './PrimaryPhoneNumberComponent';
import { Trans } from 'react-i18next';
import { GlobalContext, isAvMed } from '../../context';
import { TextNotificationsProps } from './interface';

// Helper: Calculate if phone error should be shown
const shouldShowPhoneError = (
  errors: any,
  showPreferences: boolean,
  smsConsentDetails: any
): boolean => {
  return !!(
    errors.phone ||
    (showPreferences &&
      smsConsentDetails?.optInConsent &&
      smsConsentDetails?.mobileNumber.length !== 14) ||
    (!showPreferences &&
      smsConsentDetails?.mobileNumber === '' &&
      smsConsentDetails?.optInConsent)
  );
};

// Helper: Handle phone number change
const handlePhoneInputChange = (
  value: string,
  setInValidPhoneNumber: (value: boolean) => void,
  handlePhoneNoChange: (value: string) => void
) => {
  setInValidPhoneNumber(false);
  handlePhoneNoChange(value);
};

// Helper: Handle checkbox change
const handleCheckboxChange = (
  checked: boolean,
  phoneNumber: string,
  handlePhoneNoChange: (value: string) => void,
  clearPhoneNo: () => void,
  setValue: any,
  clearErrors: any,
  setInValidPhoneNumber: (value: boolean) => void
) => {
  if (checked) {
    handlePhoneNoChange(phoneNumber);
  } else {
    clearPhoneNo();
  }
  setValue('phoneNoCheckBox', checked);
  clearErrors('phone');
  setInValidPhoneNumber(false);
};

// Helper: Handle terms and conditions change
const handleTermsChange = (
  checked: boolean,
  onPhoneNotificationsAndConsentHandle: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void,
  setValue: any,
  setSmsConsentDetails: any,
  smsConsentDetails: any,
  e: React.ChangeEvent<HTMLInputElement>
) => {
  onPhoneNotificationsAndConsentHandle(e);
  setValue('termsCondition', checked);
  setSmsConsentDetails({
    ...smsConsentDetails,
    termsAndCondition: checked,
  });
};

// Component: Phone input field section
const PhoneInputSection: React.FC<{
  showPreferences?: boolean;
  showEmptyErrorPhoneMessage: boolean;
  register: any;
  setInValidPhoneNumber: (value: boolean) => void;
  handlePhoneNoChange: (value: string) => void;
  inValidPhoneNumber: boolean;
  watch: any;
  profileState: any;
  setValue: any;
  clearErrors: any;
  clearPhoneNo: () => void;
}> = ({
  showPreferences,
  showEmptyErrorPhoneMessage,
  register,
  setInValidPhoneNumber,
  handlePhoneNoChange,
  inValidPhoneNumber,
  watch,
  profileState,
  setValue,
  clearErrors,
  clearPhoneNo,
}) => {
  return (
    <div
      className={`row ${
        showPreferences ? 'order-2 mt-n2 mb-4' : 'order-1 my-3'
      }`}
    >
      <div className="d-flex flex-lg-row flex-md-row flex-column">
        <div className="d-flex flex-column col-md-4 col-12 mb-md-0 mb-2">
          <InputField
            className={
              showEmptyErrorPhoneMessage
                ? 'form-input error-border'
                : 'form-input'
            }
            type="text"
            id="profileNumber"
            data-testid="phoneInput"
            placeholder={t('profile.PhoneNumberPlaceHolder')}
            {...register('phone', {
              required: true,
              maxLength: 14,
              minLength: 14,
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                handlePhoneInputChange(
                  e.target.value,
                  setInValidPhoneNumber,
                  handlePhoneNoChange
                );
              },
            })}
          />
          {showEmptyErrorPhoneMessage && (
            <div className="mt-1">
              <ErrorMessage>{t('profile.phoneNumber')}</ErrorMessage>
            </div>
          )}
          {inValidPhoneNumber && (
            <div className="mt-1">
              <ErrorMessage>{t('profile.phoneNumber')}</ErrorMessage>
            </div>
          )}
        </div>
        {!showPreferences && (
          <div
            className={`d-flex ms-md-3 ms-0 ${
              showEmptyErrorPhoneMessage
                ? 'align-items-start mt-2'
                : 'align-items-center'
            }`}
          >
            <Checkbox
              id="use_primary_no"
              type="checkbox"
              disabled={!profileState?.phones?.[0]?.phoneNumber}
              dataTestId="use_primary_no"
              checked={watch('phoneNoCheckBox')}
              {...register('phoneNoCheckBox', {
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  handleCheckboxChange(
                    e.target.checked,
                    profileState?.phones?.[0]?.phoneNumber ?? '',
                    handlePhoneNoChange,
                    clearPhoneNo,
                    setValue,
                    clearErrors,
                    setInValidPhoneNumber
                  );
                },
              })}
            >
              {t('profile.usePrimaryPhoneNumber')}
            </Checkbox>
          </div>
        )}
      </div>
    </div>
  );
};

// Component: Radio buttons for Yes/No consent
const ConsentRadioButtons: React.FC<{
  showPreferences?: boolean;
  smsConsentDetails: any;
  fromPreferredChannel: boolean;
  setSmsConsentDetails: any;
  setIsCheckedAlert: (value: boolean) => void;
  unregister: any;
  YesForReceiveTextMsg: React.ReactNode;
  NoForReceiveTextMsg: React.ReactNode;
}> = ({
  showPreferences,
  smsConsentDetails,
  fromPreferredChannel,
  setSmsConsentDetails,
  setIsCheckedAlert,
  unregister,
  YesForReceiveTextMsg,
  NoForReceiveTextMsg,
}) => {
  return (
    <div className={`row ${showPreferences ? 'order-1' : 'order-2'}`}>
      <div className="col-md-5 col-12 pb-3">
        <div className="d-flex">
          <Checkbox
            id="phoneNotificationYes"
            name="phoneNotification"
            className="my-4"
            type="radio"
            disabled={false}
            dataTestId="phoneNotificationYes"
            checked={smsConsentDetails?.optInConsent ?? fromPreferredChannel}
            onChange={() => {
              setSmsConsentDetails({
                ...smsConsentDetails,
                optInConsent: true,
              });
              setIsCheckedAlert(false);
            }}
          >
            {YesForReceiveTextMsg}
          </Checkbox>
        </div>
        <Paragraph
          level={1}
          text={t('profile.yesRadioContent')}
          variant="regular"
          data-testId="yesRadioContent"
          className={`lh-sm mt-1 pl-2 ${showPreferences ? '' : 'mb-0'}`}
        />
      </div>
      <div className="col-md-6 col-12 pb-3 d-flex">
        <Checkbox
          id="phoneNotificationNo"
          className="my-4"
          name="phoneNotification"
          type="radio"
          disabled={!!fromPreferredChannel}
          dataTestId="phoneNotificationNo"
          checked={!smsConsentDetails?.optInConsent && !fromPreferredChannel}
          onChange={() => {
            setSmsConsentDetails({
              ...smsConsentDetails,
              optInConsent: false,
            });
            setIsCheckedAlert(true);
            unregister('termsCondition');
          }}
        >
          {NoForReceiveTextMsg}
        </Checkbox>
      </div>
    </div>
  );
};

// Component: Terms and conditions checkbox for preferences view
const PreferencesTermsCheckbox: React.FC<{
  errors: any;
  watch: any;
  register: any;
  isScrolledToBottom: boolean;
  onPhoneNotificationsAndConsentHandle: any;
  setValue: any;
  setSmsConsentDetails: any;
  smsConsentDetails: any;
  setIsTermsModal?: (value: boolean) => void;
}> = ({
  errors,
  watch,
  register,
  isScrolledToBottom,
  onPhoneNotificationsAndConsentHandle,
  setValue,
  setSmsConsentDetails,
  smsConsentDetails,
  setIsTermsModal,
}) => {
  return (
    <div className="row order-3">
      <div className="col-12 d-flex mb-2 align-items-center">
        <Checkbox
          id="phoneNotificationsAndConsentCheck"
          className={errors.termsCondition ? 'error-input-bdr' : null}
          type="checkbox"
          checked={watch('termsCondition')}
          dataTestId="phoneNotificationsAndConsentCheck"
          {...register('termsCondition', {
            required: true,
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              handleTermsChange(
                e.target.checked,
                onPhoneNotificationsAndConsentHandle,
                setValue,
                setSmsConsentDetails,
                smsConsentDetails,
                e
              );
            },
          })}
        />
        <Paragraph
          data-testid="phoneNotificationsAndConsentText"
          level={2}
          text={
            <Trans
              i18nKey="communications.phoneNotificationTermsOfUse"
              components={{
                link1: (
                  <Link
                    onClick={() => setIsTermsModal?.(true)}
                    className="text-decoration-underline small-link"
                    data-testid="termsAndConditionLink"
                  >
                    {t('common.termsOfUse')}
                  </Link>
                ),
              }}
            />
          }
          variant="regular"
          className="mb-0 ms-2"
        />
      </div>
      {errors.termsCondition && (
        <div className="col-12 mt-1 mb-2">
          <ErrorMessage dataTestId="phoneConsent">
            {t('profile.phoneConsent')}
          </ErrorMessage>
        </div>
      )}
    </div>
  );
};

// Component: Terms and conditions section for profile view
const ProfileTermsSection: React.FC<{
  handleScroll: (e: React.UIEvent<HTMLDivElement, UIEvent>) => void;
  errors: any;
  watch: any;
  register: any;
  isScrolledToBottom: boolean;
  onPhoneNotificationsAndConsentHandle: any;
  setValue: any;
  setSmsConsentDetails: any;
  smsConsentDetails: any;
}> = ({
  handleScroll,
  errors,
  watch,
  register,
  isScrolledToBottom,
  onPhoneNotificationsAndConsentHandle,
  setValue,
  setSmsConsentDetails,
  smsConsentDetails,
}) => {
  return (
    <>
      <div className="row order-2 mb-3">
        <div className="col-12 mb-2">
          <Paragraph
            text={t('profile.phoneAcknowledgement')}
            variant="regular"
            data-testId="phoneAcknowledgement"
            className="mt-2 mt-lg-0 mt-md-0"
          />
          <div
            data-testid="phoneNotificationMessage"
            className="phone_notify_msg_wrapper notification_border"
            onScroll={handleScroll}
          >
            <div className="col-12 lineBrake">
              <Paragraph
                text={
                  <Trans
                    i18nKey="profile.phoneNotificationConsent"
                    components={{
                      link1: isAvMed() ? (
                        <Link
                          to={t('profile.paperlessTextareaUrl')}
                          title="AvMed"
                          target="_blank"
                          className="text-decoration-underline"
                        />
                      ) : (
                        <Link
                          to={`tel:${t('profile.toNumber')}`}
                          className="ws-nowrap text-decoration-underline px-1"
                        >
                          {t('profile.toNumber')}
                        </Link>
                      ),
                      link2: isAvMed() ? (
                        <Link
                          to={t('profile.paperlessTextareaUrl')}
                          title="AvMed"
                          target="_blank"
                          className="text-decoration-underline"
                        />
                      ) : (
                        <Link
                          to={`tel:${t('profile.toNumber')}`}
                          className="ws-nowrap text-decoration-underline px-1"
                        >
                          {t('profile.toNumber')}
                        </Link>
                      ),
                    }}
                  />
                }
                variant="regular"
                data-testid="phoneAcknowledgementTextArea"
                className="mb-0 lineBrake"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="row order-3">
        <div className="col-12 d-flex mb-2">
          <Checkbox
            id="phoneNotificationsAndConsentCheck"
            className={errors.termsCondition ? 'error-input-bdr' : null}
            type="checkbox"
            disabled={!isScrolledToBottom}
            checked={watch('termsCondition')}
            dataTestId="phoneNotificationsAndConsentCheck"
            {...register('termsCondition', {
              required: true,
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                handleTermsChange(
                  e.target.checked,
                  onPhoneNotificationsAndConsentHandle,
                  setValue,
                  setSmsConsentDetails,
                  smsConsentDetails,
                  e
                );
              },
            })}
          >
            {t('profile.PhoneNotificationAcceptProfile')}
          </Checkbox>
        </div>
        {errors.termsCondition && (
          <div className="col-12 mt-1 mb-2">
            <ErrorMessage dataTestId="phoneConsent">
              {t('profile.phoneConsent')}
            </ErrorMessage>
          </div>
        )}
      </div>
    </>
  );
};

// Component: Form action buttons
const FormActionButtons: React.FC<{
  disableSave: boolean;
  cancelConsent: () => void;
}> = ({ disableSave, cancelConsent }) => {
  return (
    <>
      <hr />
      <div className="row d-flex justify-content-start align-items-center py-2">
        <div className="col-sm-auto col-12 mb-sm-0 mb-3">
          <Button
            dataTestId={'saveButton'}
            variant="primary_btn"
            disabled={disableSave}
            type="submit"
          >
            {t('common.save')}
          </Button>
        </div>
        <div className="col-sm-auto col-12 mb-sm-0">
          <Button
            type="button"
            dataTestId={'cancelButton'}
            variant="secondary_btn"
            onClick={cancelConsent}
          >
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </>
  );
};

const TextNotifications: React.FC<
  TextNotificationsProps & { isDirty?: boolean }
> = ({
  showPreferences,
  showPrimaryNumber,
  setShowPrimaryNumber,
  isScrolledToBottom,
  handlePhoneNoChange,
  clearPhoneNo,
  cancelConsent,
  handleScroll,
  smsConsentDetails,
  setSmsConsentDetails,
  setIsCheckedAlert,
  inValidPhoneNumber,
  setInValidPhoneNumber,
  setIsTermsModal,
  YesForReceiveTextMsg,
  NoForReceiveTextMsg,
  loading,
  errors,
  watch,
  register,
  setValue,
  clearErrors,
  unregister,
  isValid,
  onPhoneNotificationsAndConsentHandle,
  fromPreferredChannel = false,
  onSubmit,
}) => {
  const { profileState } = useContext(GlobalContext);
  const showEmptyErrorPhoneMessage = shouldShowPhoneError(
    errors,
    showPreferences ?? false,
    smsConsentDetails
  );
  const shouldShowPhoneInput =
    !showPreferences ||
    smsConsentDetails?.optInConsent ||
    (fromPreferredChannel && showPreferences);
  const shouldShowConsentSection =
    smsConsentDetails?.optInConsent || fromPreferredChannel;
  const isSaveDisabled =
    !isValid || (fromPreferredChannel && !smsConsentDetails?.optInConsent);

  useEffect(() => {
    if (fromPreferredChannel) {
      setSmsConsentDetails((prev: any) => ({
        ...prev,
        optInConsent: true,
      }));
    }
  }, [fromPreferredChannel, setSmsConsentDetails]);

  const renderPrimaryPhoneNumber = () => {
    if (showPreferences || !profileState?.phones?.[0]?.phoneNumber) {
      return null;
    }
    return (
      <PrimaryPhoneNumberComponent
        t={t}
        phoneNumber={
          profileState?.phones?.find(
            (phone: { phoneType: string; phoneNumber: string }) =>
              phone.phoneType === 'Home'
          )?.phoneNumber ?? ''
        }
      />
    );
  };

  const renderTermsSection = () => {
    if (!shouldShowConsentSection) return null;

    if (showPreferences) {
      return (
        <PreferencesTermsCheckbox
          errors={errors}
          watch={watch}
          register={register}
          isScrolledToBottom={isScrolledToBottom}
          onPhoneNotificationsAndConsentHandle={
            onPhoneNotificationsAndConsentHandle
          }
          setValue={setValue}
          setSmsConsentDetails={setSmsConsentDetails}
          smsConsentDetails={smsConsentDetails}
          setIsTermsModal={setIsTermsModal}
        />
      );
    }

    return (
      <ProfileTermsSection
        handleScroll={handleScroll}
        errors={errors}
        watch={watch}
        register={register}
        isScrolledToBottom={isScrolledToBottom}
        onPhoneNotificationsAndConsentHandle={
          onPhoneNotificationsAndConsentHandle
        }
        setValue={setValue}
        setSmsConsentDetails={setSmsConsentDetails}
        smsConsentDetails={smsConsentDetails}
      />
    );
  };

  return (
    <div>
      <ProfileHeaderContent
        header={showPreferences ? '' : t('profile.textNotifications')}
        contentOne={t('dashboard.textNotificationsContent') ?? ''}
        dataTestIdOne="dashboard.textNotificationsContent"
        contentClassNameOne={'col-lg-9 col-12 mb-md-0 mb-3'}
        showButton={!showPrimaryNumber}
        onClick={() => setShowPrimaryNumber(true)}
        disableButton={!profileState?.isMemberEnrolled}
      />
      {(showPrimaryNumber || showPreferences) && (
        <div className="mt-4">
          <form onSubmit={onSubmit}>
            {loading && <Loader />}
            <div className="col-12 col-lg-9">
              {renderPrimaryPhoneNumber()}
              <div className="row d-flex flex-column">
                {shouldShowPhoneInput && (
                  <PhoneInputSection
                    showPreferences={showPreferences}
                    showEmptyErrorPhoneMessage={showEmptyErrorPhoneMessage}
                    register={register}
                    setInValidPhoneNumber={setInValidPhoneNumber}
                    handlePhoneNoChange={handlePhoneNoChange}
                    inValidPhoneNumber={inValidPhoneNumber}
                    watch={watch}
                    profileState={profileState}
                    setValue={setValue}
                    clearErrors={clearErrors}
                    clearPhoneNo={clearPhoneNo}
                  />
                )}
                <ConsentRadioButtons
                  showPreferences={showPreferences}
                  smsConsentDetails={smsConsentDetails}
                  fromPreferredChannel={fromPreferredChannel}
                  setSmsConsentDetails={setSmsConsentDetails}
                  setIsCheckedAlert={setIsCheckedAlert}
                  unregister={unregister}
                  YesForReceiveTextMsg={YesForReceiveTextMsg}
                  NoForReceiveTextMsg={NoForReceiveTextMsg}
                />
                {renderTermsSection()}
              </div>
            </div>
            {!showPreferences && (
              <FormActionButtons
                disableSave={isSaveDisabled}
                cancelConsent={cancelConsent}
              />
            )}
          </form>
        </div>
      )}
    </div>
  );
};

export default TextNotifications;
